import { useEffect, useState, useMemo } from 'react';
import Loading from 'components/Loading';
import { useNextStep } from 'hooks/useStep';
import { useGetQuestions } from 'api/hooks/useSurveyQuestions';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../context/userContext';
import { allQuestionsAnswered } from '../utils';
import { hasPending } from '../persistence/backgroundSaves';

const ResultsIntro = () => {
  const [completeProcess, setCompleteProcess] = useState(false);
  const { saveCompleteProcess, saveAnswersLoading, userLoading, answers, company, jobRoleId } = useUser();
  const { nextStep } = useNextStep();
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const { questions, questionLoading } = useGetQuestions({ companyId: company?.id || '', jobRoleId });

  const userId = useMemo(() => localStorage.getItem('id') || undefined, []);
  const pendingBackgroundSaves = hasPending(company?.id, userId);

  useEffect(() => {
    // While saving or pending background flushes, do not redirect to incomplete
    if (saveAnswersLoading || userLoading || questionLoading || pendingBackgroundSaves) return;
    if (!questions) return;

    if (!allQuestionsAnswered(questions, answers)) {
      navigate(`/${companySlug}/incomplete`);
    }
  }, [questions, answers, navigate, companySlug, saveAnswersLoading, userLoading, questionLoading, pendingBackgroundSaves]);

  const onComplete = () => {
    saveCompleteProcess();
    setCompleteProcess(true);
  };

  if (saveAnswersLoading || userLoading || questionLoading || pendingBackgroundSaves) {
    return (
      <div className="flex flex-col items-center justify-center gap-20 text-center text-white h-5/6">
        <div className="mb-1 text-xl font-semibold text-white sm:text-display-sm leading-xl sm:leading-display-sm">
          Please wait while we process your results
        </div>
        <p className="text-white">Thanks for waiting.</p>
        <Loading isComponent={true} />
      </div>
    );
  }

  if (completeProcess) {
    nextStep();
  }

  return (
    <div className="flex flex-col items-center justify-center gap-20 text-center text-white h-5/6">
      {!saveAnswersLoading && !completeProcess ? (
        <>
          <div className="ps-3">
            <div className="mb-1 text-xl font-semibold text-white sm:text-display-sm leading-xl sm:leading-display-sm">Almost done</div>
            <p className="text-white">Thanks for taking our test!</p>
          </div>
          <img className="w-20 h-20" src="/icon/puzzle_head.png" alt="puzzle head" />
          <p className="text-white">Please click submit to send your results</p>
          <button className="p-2 font-semibold text-black uppercase bg-yellow-500 rounded-lg text-md " type="button" onClick={() => onComplete()}>
            Submit
          </button>
        </>
      ) : null}
    </div>
  );
};

export default ResultsIntro;
